// The chat channel's session-scoped **uncertainty marker** (`beekeeper-progress-indicator` §1) —
// the one piece of state that must survive a full page reload while everything else in
// `chat-channel.ts` is rebuilt from nothing.
//
// A `submitting`/`running`/`failed` phase is safely lost on reload: a fresh channel starts `idle`
// and `rehydrate()` re-derives the truth from the server. `uncertain` is not safe to lose — a
// fresh channel defaulting to `idle` would re-enable the composer and reopen the very
// duplicate-send hole the phase exists to close. So the one fact that must outlive the tab's
// reload — "a submission's outcome is not yet known" — is persisted here, scoped per chat channel
// (one instance per sandbox, exactly like `ChatSseClient` itself) so a tournament channel's marker
// can never resolve or block a practice channel's, or vice versa.
//
// `sessionStorage`, not `localStorage` (contrast `rounds-credential-store.ts`): this is a same-tab
// fact about one in-flight request, not a durable identity that should survive the tab closing — a
// closed tab's own submission is moot, and a stale marker surviving into a new tab (or a new day)
// would lock a composer for no reason evidence could ever resolve.
//
// Follows the existing `*-credential-store.ts` pattern in every respect that still applies: a
// `StorageLike` shim accepted as a parameter, try/catch around every storage call, strict runtime
// validation of the untrusted stored value, degrade-to-absent rather than throw, and a `version`
// field so a future shape change is a migration rather than a silent misread.

import type { StorageLike } from './rounds-credential-store';

export type { StorageLike };

/** The key prefix this adapter owns. The full key is `${KEY_PREFIX}:${scope}` at construction
 * time — see {@link createChatUncertaintyMarkerStore}. */
export const CHAT_UNCERTAINTY_MARKER_KEY_PREFIX = 'waggle-chat-uncertainty';

/** The current stored-shape version. Any other value is treated as foreign/legacy and degrades to
 * "nothing stored" until a migration is written.
 *
 * Bumped 1 -> 2 to add `submittedText` (review, P1-D): a v1 marker predates that field and is
 * missing it, so it correctly degrades to "no marker" under the v2 validator rather than being
 * trusted with an absent/undefined text that would never match anything. */
export const CHAT_UNCERTAINTY_MARKER_VERSION = 2;

/**
 * A submission whose outcome is not yet known, plus the evidence `rehydrate()` needs to tell
 * "this submission settled" from "some other turn — another tab's, or one already over before
 * this send — is sitting there".
 */
export interface ChatUncertaintyMarker {
  version: number;
  /** The turn ids the thread already held at the moment the ambiguous send went out. An in-flight
   * or newly settled turn found on rehydrate that is NOT in this set is evidence about *this*
   * submission; one that IS in the set was already known about beforehand and resolves nothing
   * (`beekeeper-progress-indicator` §1: "a turn already in the baseline does NOT resolve it"). */
  baseline: readonly string[];
  /** The exact text of the player message this send submitted (review, P1-D). The baseline alone
   * narrows "which turn is this" but does not prove it: another tab could have started a turn
   * while our own POST was still racing toward admission. Requiring the observed turn's player
   * message to match this text narrows the window further — it is still not proof, since nothing
   * the server returns correlates an observed turn to *our* request. That residual gap is
   * documented at the resolution site in `chat-channel.ts`, not solved here: the complete fix is a
   * server-issued correlation/idempotency key, not more client-side text heuristics. */
  submittedText: string;
}

/**
 * Runtime-validates an untrusted parsed value, returning a fresh well-typed marker or `null` on
 * ANY failure (mirrors `validateRoundsCredential`) — a stored `null`, an array, a wrong-typed
 * field, a non-array/non-string-array baseline, a missing/non-string `submittedText`, or a
 * missing/foreign `version` all degrade to "no marker" rather than being trusted.
 */
export function validateChatUncertaintyMarker(value: unknown): ChatUncertaintyMarker | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (record.version !== CHAT_UNCERTAINTY_MARKER_VERSION) {
    return null;
  }
  if (!Array.isArray(record.baseline) || !record.baseline.every((id) => typeof id === 'string')) {
    return null;
  }
  if (typeof record.submittedText !== 'string') {
    return null;
  }
  return {
    version: CHAT_UNCERTAINTY_MARKER_VERSION,
    baseline: record.baseline as string[],
    submittedText: record.submittedText,
  };
}

/** The marker store seam, pre-scoped to one chat channel's own sandbox — the same "one instance,
 * one scope" shape `ChatSseClient` itself takes. Never throws to the caller. */
export interface ChatUncertaintyMarkerStore {
  /** The stored marker, or `null` when nothing valid is stored for this scope (never set, already
   * resolved and cleared, a cleared browser, private mode, a poisoned value). */
  get(): ChatUncertaintyMarker | null;
  /** Persists that this scope's latest submission's outcome is unknown, with the baseline and
   * submitted text captured at the moment of that submission. Overwrites whatever marker (if any)
   * was already stored — a fresh send always starts a fresh ambiguity window. */
  set(marker: { baseline: readonly string[]; submittedText: string }): void;
  /** Forgets this scope's marker. Called only on observed resolution (an in-flight or newly
   * settled turn not in the baseline, discovered by `rehydrate()`) or a demonstrably
   * pre-admission refusal — never on a timer, never on boot alone. */
  clear(): void;
}

/** Builds the guarded, scope-keyed marker store. `scope` is folded into the storage key at
 * construction, exactly like `ChatSseClient`'s own `scope` — so two channels in the same tab
 * (tournament and practice) never collide on the same `sessionStorage` entry. */
export function createChatUncertaintyMarkerStore(
  storage: StorageLike,
  scope: string,
  keyPrefix: string = CHAT_UNCERTAINTY_MARKER_KEY_PREFIX,
): ChatUncertaintyMarkerStore {
  const key = `${keyPrefix}:${scope}`;
  return {
    get(): ChatUncertaintyMarker | null {
      let raw: string | null;
      try {
        raw = storage.getItem(key);
      } catch {
        return null; // storage unavailable (private mode, a sandboxed SecurityError)
      }
      if (raw === null) {
        return null;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return null; // malformed JSON — treated as nothing stored, never repaired in place
      }
      return validateChatUncertaintyMarker(parsed);
    },
    set(marker): void {
      const stored: ChatUncertaintyMarker = {
        version: CHAT_UNCERTAINTY_MARKER_VERSION,
        baseline: marker.baseline,
        submittedText: marker.submittedText,
      };
      try {
        storage.setItem(key, JSON.stringify(stored));
      } catch {
        // Quota, private mode, sandboxed: the marker simply does not survive a reload in this
        // tab. The in-memory phase (still `uncertain` for the rest of this session) is unaffected.
      }
    },
    clear(): void {
      try {
        storage.removeItem(key);
      } catch {
        // Nothing to do: a value we cannot remove is still one we will not present again this
        // load, because the caller drops its in-memory effect too.
      }
    },
  };
}

/** The default, real `sessionStorage`-backed shim — `sessionStorage`, deliberately, not
 * `localStorage`; see this module's own header. */
export function windowSessionStorage(): StorageLike {
  return {
    getItem: (key) => window.sessionStorage.getItem(key),
    setItem: (key, value) => window.sessionStorage.setItem(key, value),
    removeItem: (key) => window.sessionStorage.removeItem(key),
  };
}
